import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box } from '@mui/material';
import CustomFieldInput from 'app/shared/components/customFields/CustomFieldsSection/components/CustomFieldInput';
import { CUSTOM_FIELDS_TARGET } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.constants';
import {
  filterDisplayableEnabledDefinitionsForTarget,
  isCalculated,
  isManualReadOnly,
  isManualRequired,
} from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.utils';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { selectCustomFields } from 'store/system/selectors/customFieldDefinitions.selectors';

import { ContentHeadline } from '../sharedComponents';

import { buildCustomFieldsEntityConfig } from './ApplicationCustomFields.config';
import { fieldItemStyles, fieldsGridStyles } from './ApplicationCustomFields.styles';

const ApplicationCustomFields = ({
  className,
  form,
  validationErrors,
  handleChange,
  setEntityConfig,
  customFieldsController,
  customFieldsPendingChanges,
  disabledPendingChangesHighlight,
}) => {
  const allDefinitions = useSelector(selectCustomFields);

  const definitions = useMemo(
    () => filterDisplayableEnabledDefinitionsForTarget(allDefinitions, CUSTOM_FIELDS_TARGET.LOAN),
    [allDefinitions],
  );

  const entityConfig = useMemo(
    () => (definitions.length ? buildCustomFieldsEntityConfig(definitions) : null),
    [definitions],
  );

  const customFieldsFormValues = form?.customFields;
  const customFieldsErrors = validationErrors?.customFields;

  const onFieldChange = useCallback(
    (definitionId, nextValue) => {
      customFieldsController.setFieldValue(definitionId, nextValue);

      const syntheticEvent = {
        target: {
          name: `customFields.${definitionId}`,
          type: 'text',
          value: nextValue,
          dataset: {},
        },
      };

      handleChange(syntheticEvent);
    },
    [customFieldsController, handleChange],
  );

  useEffect(() => {
    if (entityConfig) {
      setEntityConfig(entityConfig);
    }
  }, [entityConfig, setEntityConfig]);

  if (!definitions.length) {
    return null;
  }

  return (
    <Box className={className}>
      <ContentHeadline title="Custom Fields" />

      <Box sx={fieldsGridStyles}>
        {definitions.map(definition => {
          const fieldValue = customFieldsFormValues?.[definition.id] ?? '';
          const fieldError = customFieldsErrors?.[definition.id] ?? null;
          const pendingChangesActionType = disabledPendingChangesHighlight
            ? null
            : (customFieldsPendingChanges?.[definition.id] ?? null);

          return (
            <Box key={definition.id} sx={fieldItemStyles}>
              <CustomFieldInput
                dataTestId={`custom-fields-section-${definition.id}`}
                definition={definition}
                disabled={isManualReadOnly(definition) || isCalculated(definition)}
                errorKey={fieldError}
                pendingChangesActionType={pendingChangesActionType}
                required={isManualRequired(definition)}
                value={fieldValue}
                onBlur={_.noop}
                onChange={onFieldChange}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

ApplicationCustomFields.propTypes = {
  customFieldsController: PropTypes.shape({
    setFieldValue: PropTypes.func.isRequired,
  }).isRequired,
  disabledPendingChangesHighlight: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  setEntityConfig: PropTypes.func.isRequired,
  validationErrors: PropTypes.object.isRequired,
  className: PropTypes.string,
  customFieldsPendingChanges: PropTypes.object,
  form: PropTypes.object,
};

export default memo(ApplicationCustomFields);
